import { InfoCircleOutlined } from "@ant-design/icons"
import React from "react"
import { useTranslation } from "react-i18next"

import { ActionTooltip } from "./ActionTooltip.tsx"

interface FieldHintProps {
  /** What the figure means, in at most two sentences. */
  hint: React.ReactNode
  /** The field being explained, as the user reads it on screen. */
  field: string
  testId?: string
}

/**
 * The explanation beside an abbreviated figure or column header.
 *
 * A button, not a span: antd opens the tooltip on focus, and only a
 * focusable element can receive it.
 */
export const FieldHint = ({ hint, field, testId }: FieldHintProps) => {
  const { t } = useTranslation()

  return (
    <ActionTooltip title={hint}>
      <button
        type="button"
        aria-label={t("shared.aboutField", { field })}
        // Inside a Form.Item label, the default action focuses the field.
        onClick={(event) => event.preventDefault()}
        // A table header is a dnd-kit keyboard drag target that starts on
        // Space, and mergeHeaderKeyDown suppresses only Enter.
        onKeyDown={(event) => event.stopPropagation()}
        data-testid={testId}
        // Taller than wide: a squarer hit area swallows the label beside it.
        className="relative inline-flex flex-none cursor-help border-none bg-transparent p-0 text-xs leading-none text-current opacity-70 after:absolute after:-inset-x-2 after:-inset-y-4 after:content-['']"
      >
        <InfoCircleOutlined />
      </button>
    </ActionTooltip>
  )
}

interface HintedLabelProps {
  hint: React.ReactNode
  /** Names the field the hint explains, and labels it unless children say otherwise. */
  field: string
  children?: React.ReactNode
  testId?: string
}

/**
 * A figure, column header or totals row that carries its own explanation.
 */
export const HintedLabel = ({
  hint,
  field,
  children,
  testId
}: HintedLabelProps) => (
  // A block flex container would fill the cell and override its alignment.
  <span className="inline-flex items-center gap-2">
    {children ?? field}
    <FieldHint hint={hint} field={field} testId={testId} />
  </span>
)
